/**
 * SharedForecastTabs — Navigation tabs for all Forecast sub-pages.
 * Renders a sticky sub-header with tab bar at the top of each Forecast page.
 * Uses useLocation to detect the active tab and NavLink for navigation.
 *
 * Sticky sub-header (#720):
 * - Wrapper breaks out of parent `p-6` via `-mx-6 -mt-6`, adds own `px-6 py-3`
 * - `sticky top-0` sticks to top of <main>'s scroll container
 * - `bg-[var(--bg-page)]` + `border-b` gives clean separation from content
 *
 * #1632: Tab-Konsolidierung 6→4 (Cockpit · Review · Erstellen · Qualität).
 * #1635: IA-v2 — Grid ist als "Tagesplanung" in den Operations-Bereich gewandert
 * (eigener Sidebar-Punkt, /forecast ohne Tab-Leiste). Tabs jetzt:
 * Cockpit · Review · Bulk-Erstellung · Quality Score.
 *
 * @since v2.305.0 (sticky sub-header since v2.310.5, 4-Tab-IA since v2.789.0, IA-v2 since v2.791.0)
 */
import { useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

const TABS = [
  { key: 'cockpit', path: '/forecast-overview', paths: ['/forecast-overview'], labelKey: 'nav.forecastCockpit' },
  { key: 'review', path: '/forecast-review', paths: ['/forecast-review', '/kunden-forecasts'], labelKey: 'nav.forecastReview', badgeKey: 'review_pending' },
  { key: 'bulk', path: '/forecast/bulk', paths: ['/forecast/bulk'], labelKey: 'nav.forecastBulk' },
  { key: 'quality', path: '/forecast/quality', paths: ['/forecast/quality'], labelKey: 'nav.forecastQuality' },
];

function matchesPath(pathname, base) {
  return pathname === base || pathname.startsWith(base + '/');
}

export default function SharedForecastTabs({ badges }) {
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();

  const reviewBadge = (badges?.fc_pending || 0) + (badges?.az_pending || 0);

  return (
    <div className="sticky top-0 z-30 -mx-6 -mt-6 mb-5 bg-[var(--bg-primary,#F4F5FA)] border-b border-[var(--border-default,#E5E7EB)] px-6 py-3 flex items-center gap-3 flex-wrap">
      <div className="bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-[var(--border-default,#E5E7EB)] inline-flex gap-1 flex-wrap">
        {TABS.map(tab => {
          const isActive = tab.paths.some(p => matchesPath(location.pathname, p));
          const badge = tab.badgeKey === 'review_pending' ? reviewBadge : 0;
          return (
            <button
              key={tab.key}
              onClick={() => navigate(tab.path)}
              className={`px-4 py-2 rounded-[10px] text-sm font-medium transition-all whitespace-nowrap flex items-center gap-1.5 ${
                isActive
                  ? 'bg-white shadow-sm text-[var(--text-primary)] font-semibold dark:bg-[var(--bg-card)]'
                  : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
              }`}
            >
              {t(tab.labelKey)}
              {badge > 0 && (
                <span className="px-1.5 py-0.5 rounded-full text-[9px] font-bold text-white leading-none bg-red-500">{badge}</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
